import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    Stack,
    Typography,
} from '@mui/material';
import { ArrowBackRounded, ExpandMoreRounded } from '@mui/icons-material';
import { useEffect, useRef, useState } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import EmployeePortalClient, { EmployeeReviewDetail } from '../apiClients/EmployeePortalClient';
import BusySpinner from '../components/BusySpinner';
import { useErrorDisplay } from '../components/ErrorDisplay';

const EmployeeReviewPage = () => {
    const history = useHistory();
    const { employmentId, reviewId } = useParams<{ employmentId: string; reviewId: string }>();
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState(true);
    const [review, setReview] = useState<EmployeeReviewDetail | null>(null);
    const reviewGeneration = useRef(0);

    useEffect(() => {
        if (!employmentId)
            return;
        const generation = ++reviewGeneration.current;
        const controller = new AbortController();
        setReview(null);
        setBusy(true);
        new EmployeePortalClient().GetReview(employmentId, reviewId, controller.signal)
            .then(result => generation === reviewGeneration.current && setReview(result))
            .catch(error => {
                if (generation === reviewGeneration.current && error?.name !== 'AbortError')
                    setError(error, 'This published development record is not available to your account.');
            })
            .finally(() => generation === reviewGeneration.current && setBusy(false));
        return () => {
            controller.abort();
            reviewGeneration.current += 1;
        };
    // setError is supplied by the shared error surface and is not a data dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employmentId, reviewId]);

    if (!employmentId)
        return <Redirect to="/EmployeePortal" />;

    return <Box data-testid="employee-review-page" sx={{ p: { xs: 1.5, sm: 2.5, md: 3 } }}>
        <ErrorDisplay />
        <Button
            startIcon={<ArrowBackRounded />}
            onClick={() => history.push(`/EmployeePortal?employmentId=${encodeURIComponent(employmentId)}`)}
            sx={{ minHeight: 44, mb: 1.5 }}
        >
            Back to my development records
        </Button>
        <BusySpinner busy={busy}>
            {!review ? <Alert severity="info">Loading your published development record…</Alert> : <Stack spacing={2.5}>
                <Card variant="outlined" component="header">
                    <CardContent>
                        <Stack direction={{ xs: 'column', md: 'row' }} gap={2} justifyContent="space-between" alignItems={{ md: 'center' }}>
                            <Box>
                                <Typography variant="overline" color="text.secondary">Published development record</Typography>
                                <Typography variant="h4" component="h1" fontWeight={850}>{review.TemplateName}</Typography>
                                <Typography color="text.secondary">{review.OrganizationName} · {review.PositionTitle}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                    Published {new Date(review.PublishedDate).toLocaleDateString()}
                                </Typography>
                            </Box>
                            <Stack alignItems={{ xs: 'flex-start', md: 'center' }}>
                                <Typography variant="h2" color="primary.main">{review.OverallScore}</Typography>
                                <Typography variant="caption" color="text.secondary">Overall score</Typography>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>

                <Box component="section" aria-labelledby="review-outcomes-heading">
                    <Typography id="review-outcomes-heading" variant="h5" component="h2" fontWeight={800} sx={{ mb: 1.5 }}>
                        Development outcomes
                    </Typography>
                    {review.Groups.map(group => <Accordion key={group.Name} disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreRounded />}>
                            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ width: '100%', pr: 1 }}>
                                <Typography component="h3" fontWeight={750}>{group.Name}</Typography>
                                <Chip label={`Score ${group.Score}`} size="small" color="primary" variant="outlined" />
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={1.5}>
                                {group.Items.map(item => <Grid item xs={12} md={6} key={`${item.Type}:${item.Name}`}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography fontWeight={700}>{item.Name}</Typography>
                                            <Typography variant="caption" color="text.secondary">{item.Type}</Typography>
                                            <Typography sx={{ mt: 1 }}>{item.Selection}</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>)}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>)}
                </Box>
            </Stack>}
        </BusySpinner>
    </Box>;
};

export default EmployeeReviewPage;
